import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetSpecialOfferQuery,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import { trackOfferPage } from "../utils/analytics";
import AOS from "aos";
import "aos/dist/aos.css";

// Helper function to parse Draft.js content to HTML
const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // Use RTK Query to fetch offer
  const {
    data: offerResponse,
    isLoading: loading,
    error,
  } = useGetSpecialOfferQuery(id);
  const { data: settingsResponse } = useGetHomepageSettingsQuery();

  const offer = offerResponse?.offer || null;
  const logoUrl = settingsResponse?.settings?.branding?.logo || null;

  // Initialize AOS and track page visit
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Track offer page visit
  useEffect(() => {
    if (offer) {
      trackOfferPage(offer.id, offer.title);
    }
  }, [offer]);

  // Set page title and meta tags
  useEffect(() => {
    if (offer) {
      // Use setTimeout to ensure this runs after HeadTags component
      const timeoutId = setTimeout(() => {
        // Set page title - use meta_title if available, otherwise use offer title
        const pageTitle = offer.meta_title || offer.title || "Special Offer";
        document.title = pageTitle;

        // Remove ALL existing meta keywords tags (in case HeadTags or other components added them)
        const existingMetaKeywords = document.querySelectorAll(
          'meta[name="keywords"]'
        );
        existingMetaKeywords.forEach((tag) => tag.remove());

        // Remove existing meta description tags
        const existingMetaDescription = document.querySelectorAll(
          'meta[name="description"]'
        );
        existingMetaDescription.forEach((tag) => tag.remove());

        // Create new meta keywords tag with offer keywords
        if (offer.keywords || offer.title) {
          const metaKeywords = document.createElement("meta");
          metaKeywords.setAttribute("name", "keywords");

          // Combine offer title and keywords
          const keywords = offer.keywords
            ? `${offer.title}, ${offer.keywords}`
            : offer.title;

          metaKeywords.setAttribute("content", keywords);
          document.head.appendChild(metaKeywords);
        }

        // Create meta description tag - only use if offer has specific meta_description
        // Don't fall back to general description - if no specific one, don't set it
        if (offer.meta_description) {
          const metaDesc = document.createElement("meta");
          metaDesc.setAttribute("name", "description");
          metaDesc.setAttribute("content", offer.meta_description);
          document.head.appendChild(metaDesc);
        }
      }, 100); // Small delay to ensure HeadTags runs first

      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
        document.title = "Silver Fort";
        // Remove meta keywords on unmount
        const metaKeywords = document.querySelectorAll('meta[name="keywords"]');
        metaKeywords.forEach((tag) => tag.remove());
        // Remove meta description on unmount
        const metaDescription = document.querySelectorAll('meta[name="description"]');
        metaDescription.forEach((tag) => tag.remove());
      };
    }
  }, [offer, settingsResponse]);

  const handleBackToOffers = () => {
    navigate(getLocalizedPath("/services"));
  };

  const handleContactUs = () => {
    navigate(getLocalizedPath("/#contact"));
  };

  if (loading) {
    return (
      <div className="offer-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading offer details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="offer-detail-page">
        <div className="container">
          <div className="error-container">
            <h1>Offer Not Found</h1>
            <p>
              The offer you're looking for doesn't exist or has been removed.
            </p>
            <button className="btn" onClick={handleBackToOffers}>
              Back to Offers
            </button>
          </div>
        </div>
      </div>
    );
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="offer-detail-page">
      <Header />
      <div className="container">
        {/* Main Content */}
        <div className="offer-main-content">
          <div
            className="offer-image-section"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            {offer.image_url ? (
              <img
                src={offer.image_url}
                alt={offer.title}
                className="offer-hero-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <div
              className="offer-image-placeholder"
              style={{
                display: offer.image_url ? "none" : "flex",
              }}
            >
              <div className="placeholder-content">
                <div className="placeholder-icon">📸</div>
                <p>{t("common.noImageAvailable")}</p>
              </div>
            </div>
          </div>

          <div
            className="offer-details"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <h1 className="offer-title">
              {isArabic && offer.title_ar ? offer.title_ar : offer.title}
            </h1>
            <div className="offer-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: parseDraftContent(
                    isArabic && offer.content_ar
                      ? offer.content_ar
                      : offer.content
                  ),
                }}
              />
            </div>

            <div className="offer-cta" data-aos="fade-up" data-aos-delay="400">
              <div className="cta-content">
                <h2>
                  {offer.cta_title && (isArabic ? offer.cta_title_ar || offer.cta_title : offer.cta_title)
                    ? (isArabic ? offer.cta_title_ar || offer.cta_title : offer.cta_title)
                    : t("offers.interestedInOffer")}
                </h2>
                <p>
                  {offer.cta_description && (isArabic ? offer.cta_description_ar || offer.cta_description : offer.cta_description)
                    ? (isArabic ? offer.cta_description_ar || offer.cta_description : offer.cta_description)
                    : t("offers.offerDescription")}
                </p>

                <div className="cta-buttons">
                  {offer.cta_button1_link ? (
                    <a
                      href={offer.cta_button1_link}
                      className="btn btn-primary"
                      onClick={(e) => {
                        if (offer.cta_button1_link.startsWith('/')) {
                          e.preventDefault();
                          navigate(offer.cta_button1_link);
                        }
                      }}
                    >
                      {offer.cta_button1_text && (isArabic ? offer.cta_button1_text_ar || offer.cta_button1_text : offer.cta_button1_text)
                        ? (isArabic ? offer.cta_button1_text_ar || offer.cta_button1_text : offer.cta_button1_text)
                        : t("contact.contactUsNow")}
                    </a>
                  ) : (
                    <button className="btn btn-primary" onClick={handleContactUs}>
                      {offer.cta_button1_text && (isArabic ? offer.cta_button1_text_ar || offer.cta_button1_text : offer.cta_button1_text)
                        ? (isArabic ? offer.cta_button1_text_ar || offer.cta_button1_text : offer.cta_button1_text)
                        : t("contact.contactUsNow")}
                    </button>
                  )}
                  {offer.cta_button2_link ? (
                    <a
                      href={offer.cta_button2_link}
                      className="btn btn-secondary"
                      onClick={(e) => {
                        if (offer.cta_button2_link.startsWith('/')) {
                          e.preventDefault();
                          navigate(offer.cta_button2_link);
                        }
                      }}
                    >
                      {offer.cta_button2_text && (isArabic ? offer.cta_button2_text_ar || offer.cta_button2_text : offer.cta_button2_text)
                        ? (isArabic ? offer.cta_button2_text_ar || offer.cta_button2_text : offer.cta_button2_text)
                        : t("offers.viewAll")}
                    </a>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      onClick={handleBackToOffers}
                    >
                      {offer.cta_button2_text && (isArabic ? offer.cta_button2_text_ar || offer.cta_button2_text : offer.cta_button2_text)
                        ? (isArabic ? offer.cta_button2_text_ar || offer.cta_button2_text : offer.cta_button2_text)
                        : t("offers.viewAll")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Buttons */}
      <FloatingButtons />
    </div>
  );
};

export default OfferDetail;
